import React, { useEffect, useState } from 'react';
import Button, { ButtonGroup } from '@atlaskit/button';
import { ModalTransition } from '@atlaskit/modal-dialog';
import FeedbackCollector from './feedback-collector';
import { JSD_URL } from './constants';

import {
  ContextualAnalyticsData,
  MODAL,
  fireUIAnalytics,
  FireTrackAnalytics,
} from '@atlassian/analytics-bridge';

export enum FeedbackSentiment {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
  UNDEFINED = 'UNDEFINED',
}

const FEEDBACK_FIELDS_ID = {
  customfield_10047: 'customfield_10047',
  summary: 'summary',
  description: 'description',
  email: 'email',
  componentName: 'componentName',
  pageTabName: 'pageTabName',
} as const;

type FeedbackFields = keyof typeof FEEDBACK_FIELDS_ID;

interface JSDPayloadType {
  fields: Array<FieldInfo>;
}
interface FieldInfo {
  id: FeedbackFields;
  value: string;
}

const FeedbackModal = ({ pageUrl, subpageTabName, tabName, title }) => {
  const [buttonClicked, setButtonClicked] = useState<FeedbackSentiment>(
    FeedbackSentiment.UNDEFINED,
  );
  const [showModal, setShowModal] = useState(false);
  const [, setConsumerFeedback] = useState<string>('');

  useEffect(() => {
    setButtonClicked(FeedbackSentiment.UNDEFINED);
  }, [subpageTabName, tabName]);

  const updateModalVal = (value) => {
    setConsumerFeedback(value);
  };

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  /**
   * This sends a request to the production JSD widget: https://customerfeedback.atlassian.net/jira/servicedesk/projects/ADS/queues/custom/647
   */
  const onSubmit = async (
    e: React.FormEvent,
    consumerFeedback: string,
    successFeedback: () => void,
    failureFeedback: () => void,
  ) => {
    const feedbackPayload = {
      fields: [
        {
          id: FEEDBACK_FIELDS_ID.summary,
          value: consumerFeedback
            ? consumerFeedback.slice(0, 100)
            : 'EMPTY FEEDBACK',
        },
        {
          id: FEEDBACK_FIELDS_ID.description,
          value: consumerFeedback ? consumerFeedback : 'EMPTY FEEDBACK',
        },
        {
          id: FEEDBACK_FIELDS_ID.email,
          // This is a placeholder email since it is required for the Jira Widget
          value: 'do-not-reply@atlassian.com',
        },
        {
          /**
           * This id links to the "Feedback Context" field. This is needed as you cannot add your own custom fields to the
           * feedback JSD. All the fields above are built into the JSD, these ones below are required for our own purpose.
           */

          id: FEEDBACK_FIELDS_ID.customfield_10047,
          value: `Environment: ${process.env.NODE_ENV}
            Feedback: ${buttonClicked}
            Page name: ${title}
            Tab name: ${
              tabName ? tabName : subpageTabName ? subpageTabName : 'N/A'
            }
            Page URL: ${pageUrl}`,
        },
      ],
    };

    const submitToJSD = async (url: string, body: JSDPayloadType) => {
      let response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
    };

    e.preventDefault();

    submitToJSD(JSD_URL, feedbackPayload)
      .then(successFeedback)
      .catch((e) => {
        failureFeedback();
      });
  };

  return (
    <>
      <ContextualAnalyticsData sourceType={MODAL} sourceName="feedback">
        <ButtonGroup>
          <Button
            testId="helpful-button"
            onClick={(e, analyticsEvent) => {
              setButtonClicked(FeedbackSentiment.POSITIVE);
              openModal();
              // https://data-portal.internal.atlassian.com/analytics/registry/41589
              fireUIAnalytics(
                analyticsEvent,
                'button clicked',
                'clicksHelpfulButton',
                {
                  value: buttonClicked,
                  title: title,
                  tabName: tabName
                    ? tabName
                    : subpageTabName
                    ? subpageTabName
                    : 'N/A',
                  pageUrl: pageUrl,
                },
              );
            }}
            isSelected={
              buttonClicked === FeedbackSentiment.POSITIVE ? true : false
            }
          >
            Yes
          </Button>
          <Button
            testId="unhelpful-button"
            onClick={(e, analyticsEvent) => {
              setButtonClicked(FeedbackSentiment.NEGATIVE);
              openModal();
              // https://data-portal.internal.atlassian.com/analytics/registry/41590
              fireUIAnalytics(
                analyticsEvent,
                'button clicked',
                'clicksUnhelpfulButton',
                {
                  value: buttonClicked,
                  title: title,
                  tabName: tabName
                    ? tabName
                    : subpageTabName
                    ? subpageTabName
                    : 'N/A',
                  pageUrl: pageUrl,
                },
              );
            }}
            isSelected={
              buttonClicked === FeedbackSentiment.NEGATIVE ? true : false
            }
          >
            No
          </Button>
        </ButtonGroup>
        <ModalTransition>
          {showModal && (
            <>
              {/* https://data-portal.internal.atlassian.com/analytics/registry/41531 */}
              <FireTrackAnalytics action="sent" actionSubject="request" />
              <FeedbackCollector
                testId="feedback-collector"
                onClose={closeModal}
                onSubmitFeedback={onSubmit}
                updateModalVal={updateModalVal}
              />
            </>
          )}
        </ModalTransition>
      </ContextualAnalyticsData>
    </>
  );
};

export default FeedbackModal;
